import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { kSuiteFormGroup, kSuiteFormGroupValues } from './ksuite-left-block.helper';

import { PhoneNumberUtil } from 'google-libphonenumber';
import { NotificationService, ValidationCheck } from '@infomaniak/angular-common';
import { translate } from '@ngneat/transloco';
import { ICountry } from '@app/models/addresses';
import { SignupFormService } from '@app/signup/shared/signup-form.service';
import { SignupService } from '@app/signup/shared/signup.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ScrollService } from '@app/core/scroll.service';
import {
  ICheckDuplicateAccountPayload,
  ICheckDuplicateAccountResult,
  ICheckDuplicateUserPayload,
  ICheckDuplicateUserResult,
} from '@app/models/signup';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SignupDuplicatePersonnalDialogComponent } from '../../dialogs/duplicate-personnal/duplicate-personnal.component';
import { SignupDuplicateOrganisationDialogComponent } from '../../dialogs/duplicate-organisation/duplicate-organisation.component';
import { VatValidator } from '@app/signup/shared/vat-validator';
import { LoaderService } from '@app/core/loader.service';
import { getPasswordValidationMessage } from '@app/shared/password-validation';
import { ConfigService } from '@infomaniak/angular-common';
import {getVATCountryShort} from "@app/signup/shared/get-vat-country-short.helper";

@Component({
  selector: 'app-signup-leftblock-ksuite',
  templateUrl: './ksuite.component.html',
  styleUrls: ['./ksuite.component.scss'],
})
export class SignupLeftBlockKsuiteComponent implements OnInit {
  @ViewChild('street2') street2: ElementRef;

  public ksuiteFormGroup = kSuiteFormGroup;
  public ksuiteFormGroupValues = kSuiteFormGroupValues;
  public isVatNumberFocused = false;
  public vatNumber: FormControl = new FormControl(
      '',
      {
        asyncValidators: this.vatValidator.validate.bind(this),
      }
    );

  // checked values for duplicate account or user
  checkedValues = {
    account: {
      name: '',
      country_id: 0,
    },
    user: {
      firstname: '',
      lastname: '',
      reminder_phone: '',
    },
    accountCallback: {
      callback: void 0,
      params: void 0,
    },
    userCallback: {
      callback: void 0,
      params: void 0,
    },
  };

  // complementary informations about address
  displayStreet3 = false;

  // Phone
  telOptions;
  country = window['COUNTRY'].toLowerCase();

  // Password
  passwordValidation = new ValidationCheck(true);

  // Country
  filteredCountries: ICountry[] = [];
  selectedCountry; // used by vatValidator

  constructor(
    private signupFormService: SignupFormService,
    private signupService: SignupService,
    private vatValidator: VatValidator,
    private scroll: ScrollService,
    private dialog: MatDialog,
    private loader: LoaderService,
    private notificationService: NotificationService,
    private configService: ConfigService,
  ) {
    this.user
      .get('password')
      .setValidators([
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(72),
        this.passwordValidator.bind(this),
      ]);
  }

  get displayFormError(): boolean {
    const passwordCtrl = this.user.get('password');
    return (
      passwordCtrl &&
      passwordCtrl.invalid &&
      passwordCtrl.touched &&
      passwordCtrl.dirty &&
      !passwordCtrl.hasError('required') &&
      !passwordCtrl.hasError('maxlength')
    );
  }

  get account(): AbstractControl {
    return this.ksuiteFormGroup.get('account');
  }

  get user(): AbstractControl {
    return this.ksuiteFormGroup.get('user');
  }

  get legalEntityTypeValue() {
    return this.account.get('legal_entity_type').value;
  }

  ngOnInit(): void {
    this.scroll.scrollToTop();
    this.telOptions = {
      preferredCountries: ['ch', 'fr', 'de'],
      nationalMode: false,
      enableAutoCountrySelect: true,
      initialCountry: this.country,
      utilsScript: 'assets/scripts/utils.js',
    };

    setTimeout(() => {
      this.user
        .get('reminder_phone')
        .setValue(`+${PhoneNumberUtil.getInstance().getCountryCodeForRegion(this.country)} `);
    });

    if (this.signupFormService.countries.length <= 0) {
      this.signupFormService.getCountries().then(() => {
        this.handleCountryLoading();
      });
    } else {
      this.handleCountryLoading();
    }
  }

  goToLogin() {
    window.location.assign(window?.['ENDPOINTS']?.login || 'welcome.infomaniak.com/auth/login');
  }

  getValidationErrorMessage() {
    return getPasswordValidationMessage(this.passwordValidation);
  }

  generate($event) {
    this.user.get('password')?.patchValue($event);
    this.user.updateValueAndValidity();
  }

  displayStreet() {
    if (this.account.get('street2').value) {
      this.displayStreet3 = true;
    } else {
      this.street2.nativeElement.focus();
    }
  }

  hideStreet() {
    this.account.get('street3').reset();
    this.displayStreet3 = false;
  }

  findCountry(inputCountry) {
    return this.signupFormService.countries.find((country: any) => country.id === inputCountry.id);
  }

  handleCountryLoading() {
    this.filteredCountries = this.signupFormService.countries.map((country) => {
      return { ...country, name: `${country.name.charAt(0).toUpperCase()}${country.name.slice(1).toLowerCase()}` };
    });
    const selectedCountry = this.signupFormService.getCurrentCountry();

    this.patchCountry(selectedCountry);
  }

  handleCountry(ev: any) {
    try {
      const selectedCountry = this.findCountry(ev.option.value);

      this.patchCountry(selectedCountry);
      // this.form.get('vat_number').updateValueAndValidity();
    } catch (e) {
      console.warn(e);
    }
  }

  filterCountries(ev) {
    this.filteredCountries = this.signupFormService.getFilteredCountries(ev);
    if (!this.filteredCountries.map((x) => x?.name).includes(ev.toUpperCase())) {
      this.account.get('country_id').setErrors({
        invalid: true,
      });
      this.account.get('country').setErrors({
        invalid: true,
      });
      this.account.updateValueAndValidity();
    }
  }

  patchCountry(selectedCountry: ICountry) {
    const name = translate(selectedCountry?.name);
    this.account
      .get('country')
      .patchValue(selectedCountry && `${name.charAt(0).toUpperCase()}${name.slice(1).toLowerCase()}`);
    this.account.get('country_id').patchValue(selectedCountry?.id);
    this.account.updateValueAndValidity();
  }

  showTVA() {
    const isIndividual = this.account.get('legal_entity_type').value === 'individual';
    const selectedCountry = this.findCountry({ id: this.account.get('country_id').value });
    this.selectedCountry = selectedCountry;
    const shouldShowTVA = !isIndividual && selectedCountry?.short !== 'CH' && selectedCountry?.short !== 'LI';

    this.vatNumber.setAsyncValidators(shouldShowTVA ? this.vatValidator.validate.bind(this) : void 0);
    return shouldShowTVA;
  }

  isEU() {
    const euCountries = [
      'AT',
      'BE',
      'BG',
      'CY',
      'CZ',
      'DE',
      'DK',
      'EE',
      'ES',
      'FI',
      'FR',
      'GR',
      'HR',
      'HU',
      'IE',
      'IT',
      'LT',
      'LU',
      'LV',
      'MT',
      'NL',
      'PL',
      'PT',
      'RO',
      'SE',
      'SI',
      'SK',
    ];
    const selectedCountry = this.findCountry({ id: this.account.get('country_id').value });

    return euCountries.includes(selectedCountry.short);
  }

  VATPrefix(country: string): string {
    return getVATCountryShort(country)?.toUpperCase();
  }

  get displayPrefixVAT(): boolean {
    return (this.vatNumber.value !== '' || this.isVatNumberFocused) && this.selectedCountry.short !== 'EE';
  }

  userLogin({ password, user, url }): Promise<any> {
    this.loader.set(true);
    return this.signupService
      .autoLogin({
        password,
        user,
        url,
      })
      .catch((error) => this.handleError(error));
  }

  formValue() {
    const formValue = this.ksuiteFormGroup.value;
    formValue.user.reminder_phone = `+${formValue.user.reminder_phone.replace(/[^\d]/gi, '')}`;

    if (formValue.account.legal_entity_type === 'individual') {
      formValue.account.name = `${formValue.user.firstname} ${formValue.user.lastname}`;
    }

    return formValue;
  }

  checkDuplicateUser(formValue): Promise<boolean | void> {
    const payload: ICheckDuplicateUserPayload = {
      user: {
        firstname: formValue.user.firstname,
        lastname: formValue.user.lastname,
        reminder_phone: formValue.user.reminder_phone,
      },
    };

    if (
      this.checkedValues.user?.firstname === payload.user.firstname &&
      this.checkedValues.user?.lastname === payload.user.lastname &&
      this.checkedValues.user?.reminder_phone === payload.user.reminder_phone
    ) {
      return Promise.resolve(false);
    }
    this.checkedValues.user = { ...payload.user };

    this.loader.set(true);
    return this.signupService
      .checkDuplicateUser(payload)
      .then((result: ICheckDuplicateUserResult) => {
        if (result.data && result.data.length > 0) {
          this.checkedValues.userCallback = {
            callback: this.openDuplicatePersonnalModal.bind(this),
            params: [result.data, `${payload.user.firstname} ${payload.user.lastname}`],
          };

          this.setFormControlError(this.ksuiteFormGroup.get('user').get('firstname'), 'validation_rule_unique');
          this.setFormControlError(this.ksuiteFormGroup.get('user').get('lastname'), 'validation_rule_unique');

          this.loader.set(false);
          return true;
        }

        this.checkedValues.userCallback = {
          callback: void 0,
          params: void 0,
        };
        this.loader.set(false);
        return false;
      })
      .catch((error) => this.handleError(error));
  }

  //#region duplicateuser

  openDuplicatePersonnalModal(duplicateUserList, userName: string) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = ['dialog--small', 'dialog--header', 'dialog--footer'];
    dialogConfig.data = {
      duplicateUsers: duplicateUserList,
      userName: userName,
    };
    const dialogRef = this.dialog.open(SignupDuplicatePersonnalDialogComponent, dialogConfig);
  }

  checkDuplicateAccount(formValue): Promise<boolean | void> {
    let name = formValue.account.name;

    if (!name || name === '') {
      name = `${formValue.user.firstname} ${formValue.user.lastname}`;
    }

    const payload: ICheckDuplicateAccountPayload = {
      account: {
        name,
        country_id: formValue.account.country_id,
      },
    };

    // Check for duplicate already done ?
    if (
      this.checkedValues.account?.country_id === payload.account.country_id &&
      this.checkedValues.account?.name === payload.account.name
    ) {
      return Promise.resolve(false);
    }

    this.checkedValues.account = { ...payload.account };

    this.loader.set(true);
    return this.signupService
      .checkDuplicateAccount(payload)
      .then((result: ICheckDuplicateAccountResult) => {
        if (result.data && result.data.length > 0) {
          this.checkedValues.accountCallback = {
            callback: this.openDuplicateOrganisationModal.bind(this),
            params: [result.data, name],
          };

          this.setFormControlError(this.ksuiteFormGroup.get('account').get('name'), 'validation_rule_unique');

          this.loader.set(false);
          return true;
        }

        this.checkedValues.accountCallback = {
          callback: void 0,
          params: void 0,
        };
        this.loader.set(false);
        return false;
      })
      .catch((error) => this.handleError(error));
  }

  //#endregion

  //#region duplicateaccount

  openDuplicateOrganisationModal(duplicateAccounts, accountName) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = ['dialog--small', 'dialog--header', 'dialog--footer'];
    dialogConfig.data = {
      duplicateAccounts,
      accountName,
    };
    const dialogRef = this.dialog.open(SignupDuplicateOrganisationDialogComponent, dialogConfig);
  }

  async submit() {
    if (this.showTVA()) {
      const VAT = this.vatNumber.value;
      if (VAT) {
        const countryShort = getVATCountryShort(this.selectedCountry.short);
        this.account.get('vat_number').patchValue(countryShort + VAT);
      }
    } else {
      this.account.get('vat_number').patchValue('');
    }

    if (!this.account.valid || !this.vatNumber.valid || !this.user.valid || !this.ksuiteFormGroup.valid) {
      this.ksuiteFormGroup.markAllAsTouched();
      this.account.markAllAsTouched();
      this.user.markAllAsTouched();
      this.notificationService.error(translate('Certaines informations ne semblent pas valides.'));
      return;
    }

    if (await this.checkDuplicateUser(this.formValue())) {
      return;
    }

    if (
      this.account.get('legal_entity_type').value !== 'individual' &&
      (await this.checkDuplicateAccount(this.formValue()))
    ) {
      return;
    }

    this.loader.set(true);
    this.signupService
      .createIndividualAccount(this.formValue(), true)
      .then((resp) => {
        sessionStorage.setItem('redirect', resp.data && resp.data.redirect);
        if (resp.data && resp.data.login) {
          this.userLogin({
            user: this.ksuiteFormGroup.get('user').get('email').value,
            password: this.ksuiteFormGroup.get('user').get('password').value,
            url: resp.data.login,
          })
            .then(() => {
              window.location.assign('/auth/login');
            })
            .catch((error) => this.handleError(error));
        } else {
          // rm loader
        }
      })
      .catch((error) => {
        // rm loader & check error
        this.handleError(error);
      });
  }

  //#region

  useCheckedValueCallback({ callback, params }) {
    return callback(...params);
  }

  handleError(obj: HttpErrorResponse) {
    this.loader.set(false);
    const findErrors = obj.error?.error?.errors || obj.error?.errors || [obj.error];

    findErrors.forEach((error) => {
      if(!error.context?.attribute) {
        this.configService.displayErrors(error);
        return;
      }

      const targetedFormControl = this.parseFormControlFromString(error.context.attribute);
      this.setFormControlError(targetedFormControl, error.code);
    });
  }

  clearErrors(formControl: AbstractControl) {
    formControl.setErrors(null);
    formControl.updateValueAndValidity();
  }

  parseFormControlFromString(attribute: string): AbstractControl {
    const target = attribute.split('.');
    return this.ksuiteFormGroup.get(target[0]).get(target[1]);
  }

  setFormControlError(formControl: AbstractControl, error: string) {
    let formControlError = {};

    switch (error) {
      case 'validation_rule_unique':
        formControlError = { unique: true };
        break;
      default:
        formControlError = { required: true };
        this.notificationService.error(translate(error));
        break;
    }

    formControl.setErrors(formControlError);
    this.scroll.scrollToTop();
  }

  private passwordValidator(ctrl: FormControl): ValidationErrors | null {
    return this.passwordValidation.validatePassword(ctrl.value) ? null : { invalid: true };
  }
}
